import type {
  HookCallbackMatcher,
  SDKResultMessage,
  SDKSession,
  SDKSessionOptions,
} from '@anthropic-ai/claude-agent-sdk';
import { unstable_v2_createSession } from '@anthropic-ai/claude-agent-sdk';

import type { EventBus } from '../events/bus';
import type { EmitContext } from '../events/emit';
import { createEnvelope } from '../events/emit';
import { hashString } from '../utils/hash';

export type ClaudeRunOptions = {
  message: string;
  model: string;
  session?: ClaudeSession;
  mcpServers?: Record<string, unknown>;
  permissionMode?: SDKSessionOptions['permissionMode'];
  allowedTools?: string[];
  disallowedTools?: string[];
  canUseTool?: SDKSessionOptions['canUseTool'];
  hooks?: SDKSessionOptions['hooks'];
  maxTurns?: number;
  maxBudgetUsd?: number;
  maxThinkingTokens?: number;
};

export type ClaudeSession = SDKSession;

export type ClaudeSessionOptions = SDKSessionOptions & {
  mcpServers?: Record<string, unknown>;
  maxTurns?: number;
  maxBudgetUsd?: number;
  maxThinkingTokens?: number;
};

export function createClaudeSession(options: ClaudeSessionOptions): ClaudeSession {
  return unstable_v2_createSession(options);
}

export async function runClaudePrompt(
  options: ClaudeRunOptions,
): Promise<SDKResultMessage> {
  const sessionOptions: ClaudeSessionOptions = {
    model: options.model,
    ...(options.permissionMode ? { permissionMode: options.permissionMode } : {}),
    ...(options.allowedTools ? { allowedTools: options.allowedTools } : {}),
    ...(options.disallowedTools ? { disallowedTools: options.disallowedTools } : {}),
    ...(options.canUseTool ? { canUseTool: options.canUseTool } : {}),
    ...(options.hooks ? { hooks: options.hooks } : {}),
    ...(options.mcpServers ? { mcpServers: options.mcpServers } : {}),
    ...(typeof options.maxTurns === 'number' ? { maxTurns: options.maxTurns } : {}),
    ...(typeof options.maxBudgetUsd === 'number'
      ? { maxBudgetUsd: options.maxBudgetUsd }
      : {}),
    ...(typeof options.maxThinkingTokens === 'number'
      ? { maxThinkingTokens: options.maxThinkingTokens }
      : {}),
  };

  const session = options.session ?? createClaudeSession(sessionOptions);
  const shouldClose = !options.session;
  try {
    await session.send(options.message);
    for await (const message of session.stream()) {
      if (message.type === 'result') {
        return message;
      }
    }
  } finally {
    if (shouldClose) {
      session.close();
    }
  }
  throw new Error('Claude session ended without a result');
}

export function createToolHooks(options: {
  bus?: EventBus;
  context: EmitContext;
  onHeartbeat?: () => Promise<void>;
  onToolCall?: (entry: {
    toolCallId: string;
    toolName: string;
    argsDigest: string;
    resultDigest?: string;
    ok: boolean;
  }) => void;
}): Partial<Record<string, HookCallbackMatcher[]>> {
  if (!options.bus) return {};

  const preToolUse: HookCallbackMatcher = {
    hooks: [
      async (input) => {
        if (input.hook_event_name !== 'PreToolUse') return { continue: true };
        await options.onHeartbeat?.();
        const argsDigest = hashString(JSON.stringify(input.tool_input ?? {}));
        await options.bus?.emit(
          createEnvelope({
            type: 'ai.tool_call_started',
            source: 'ai',
            level: 'info',
            context: options.context,
            payload: {
              toolCallId: input.tool_use_id,
              toolName: input.tool_name,
              argsDigest,
            },
          }),
        );
        return { continue: true };
      },
    ],
  };

  const postToolUse: HookCallbackMatcher = {
    hooks: [
      async (input) => {
        if (input.hook_event_name !== 'PostToolUse') return { continue: true };
        await options.onHeartbeat?.();
        const resultDigest = hashString(JSON.stringify(input.tool_response ?? {}));
        await options.bus?.emit(
          createEnvelope({
            type: 'ai.tool_call_finished',
            source: 'ai',
            level: 'info',
            context: options.context,
            payload: {
              toolCallId: input.tool_use_id,
              ok: true,
              resultDigest,
            },
          }),
        );
        options.onToolCall?.({
          toolCallId: input.tool_use_id,
          toolName: input.tool_name,
          argsDigest: hashString(JSON.stringify(input.tool_input ?? {})),
          resultDigest,
          ok: true,
        });
        return { continue: true };
      },
    ],
  };

  const postToolUseFailure: HookCallbackMatcher = {
    hooks: [
      async (input) => {
        if (input.hook_event_name !== 'PostToolUseFailure') return { continue: true };
        await options.onHeartbeat?.();
        await options.bus?.emit(
          createEnvelope({
            type: 'ai.tool_call_finished',
            source: 'ai',
            level: 'error',
            context: options.context,
            payload: {
              toolCallId: input.tool_use_id,
              ok: false,
              error: { message: input.error ?? 'Tool call failed' },
            },
          }),
        );
        options.onToolCall?.({
          toolCallId: input.tool_use_id,
          toolName: input.tool_name,
          argsDigest: hashString(JSON.stringify(input.tool_input ?? {})),
          ok: false,
        });
        return { continue: true };
      },
    ],
  };

  return {
    PreToolUse: [preToolUse],
    PostToolUse: [postToolUse],
    PostToolUseFailure: [postToolUseFailure],
  };
}
